<template>
    <div
        :class="context.classes.element"
        class="p-1"
        :data-type="context.type"
    >
        <div v-if="loadingOptions || !initialCheckComplete" key="loadingOptions" class="">
            <b-spinner small></b-spinner>
        </div>
        <div 
            v-else-if="selectedVendorId" 
            :key="selectedVendorId" 
            class=""
        >
            <div v-if="optionsDisplay.length" class="contact-picker-checkbox-prime-group mb-3">
                <div 
                    v-for="tuObj in optionsDisplay" 
                    :key="tuObj.id" 
                    :class="[
                        selected.includes(tuObj.id) ? 'is-checked' : 'not-checked', 
                        (readOnly || isOptionDisabled(tuObj)) ? 'is-readonly' : 'is-enabled',
                        isOptionDisabled(tuObj) ? 'is-disabled' : ''
                    ]"
                    class="field-checkbox vendor-contact-checkbox"
                >
                    <Checkbox 
                        :id="tuObj.id" 
                        name="vendor_contacts" 
                        :value="tuObj.id" 
                        :modelValue="selected"
                        @input="setSelected"
                        :disabled="readOnly || isOptionDisabled(tuObj)"
                    />  <!-- v-model="selected" we opted for 1-way binding with @input event handler -->
                    <label 
                        :for="tuObj.id" 
                        class="mb-0"
                    >
                        {{ tuObj.full_name }}
                        <div v-if="showDefaults" class="d-inline-block">
                            <div class="d-flex gap-2 mx-2 flex-wrap">
                                <b-badge 
                                    v-for="role in (tuObj.tenant_roles || [])" 
                                    :key="role.slug" 
                                    :variant="selectDefaultCategories.includes(role.slug) ? 'soft-primary' : 'soft-secondary'" 
                                    class="text-uppercase"
                                >{{ role.name }}</b-badge>
                            </div>
                        </div>
                    </label>
                </div>
            </div>
            <div v-else key="noVendorMemberOptions" class="text-muted mb-2">
                No existing contacts at this vendor
            </div>
            <!-- ADD NEW CONTACT - keep inside of element that has key to change when selected vendor changes -->
            <div v-if="selectedVendorId && !noAdd">
                <b-button v-if="!readOnly" size="sm" variant="subtle-primary" @click="$refs.createContactModal.startCreate()">
                    <b-icon icon="plus-circle" class="mr-1"></b-icon>
                    Add New Contact
                </b-button>
                <contact-create-update-modal
                    ref="createContactModal"
                    v-if="!readOnly"
                    :new-data="{company: selectedVendorId}"
                    :modal-title="`Add new contact`"
                    hide-company-input
                    save-close-html="Save"
                    hide-save-redirect
                    email-required
                    @create-complete="handleNewVendorContact"
                ></contact-create-update-modal>
            </div>
        </div>
        <div v-else class="">
            No vendor selected
        </div>
    </div>
</template>

<script>
import { errorHandling } from '../../js/custom-vue-mixins';
import { suppliosApi } from '../../js/api-service';
import Checkbox from 'primevue/checkbox';

// formulate custom inputs:  must read from and write to context.model
// for this one, we'll assume that is only the ID of the company/vendor
// then we need to get options as needed, from here
// no options passed in from parent?  or have a prop set to fetch companies vs. get from prop?

export default {
    name: 'FormulateVendorContactPicker',
    components: {
        Checkbox,
    },
    mixins: [
        errorHandling,
    ],
    props: {
        context: {
            type: Object,
            required: true,
        },
        // vendorId -- pass in, or set vendorField and we find vendorId from that
        vendorId: {
            type: String,
            required: false,
        },
        // vendorField -- name of the field on the SAME FORM that has vendorId as it's value
        // if that value changes, it will refetch the options
        vendorField: {
            type: String,
            default: 'vendor',
        },
        // if true, there will be no 'add a contact' button / capability
        noAdd: {
            type: Boolean,
            default: false,
        },
        selectDefaults: {
            // if you want to auto-select contacts who hold certain TenantRoles - specify the role SLUGS here - single string OR array of strings
            // 'ext_quality' or ['ext_quality', 'ext_sourcing'], etc.
            type: [String, Array],
            required: false,
        },
        showDefaults: { // should we show the TenantRoles each contact holds (as badges with the role name)?
            type: Boolean,
            default: false,
        },
        disabledField: {
            // field/attribute in EACH OPTION that means 'disabled' (when its true)
            type: String,
            default: 'unassignable',
        },
        // purposely not using 'options' prop here -- always fetch from API
    },
    data() {
        return {
            loadingOptions: false,
            initialSelected: [], // the INITIAL values -- IDs of contacts -- we don't know if they match the initial vendor ID - have to fetch and check first
            initialCheckComplete: false,  // set true once we've checked if initial selected contacts are in initial selected vendor
            fetchedOptions: null,  // we fetch when user clicks on dropdown - use null until we know we've fetched them
            selected: [],  // v-model with the checkboxes
            selectedVendorId: '',
            formComponent: null,  // once you go up the tree to find parent, stash reference to it here
            formComponentInitialData: null,  // the INITAL data of form component - need this before each component reports up its v-model value
            formComponentData: null,  // from formComponent - this is the LIVE data (FormulateForm.proxy) -- but not the INITIAL DATA
        }
    },
    computed: {
        optionsDisplay() {  // the ones we actually display -- includes disabled ones and one extra 'LOADING' option
            // warning... if you set 'classification' to something that does not have options prop, then context.options = false
            return this.fetchedOptions ?? [{id: 'LOADING'}];  // should be array of option objects - need to transform/rekey?
        },
        availableOptions() {
            // TU options that are NOT disabled -- also filter out any that are not in the selected vendor
            const opts = this.fetchedOptions ?? [];
            return opts.filter(opt => {
                return !this.isOptionDisabled(opt) && opt.company == this.selectedVendorId;
            });
        },
        availableOptionIds() {
            // option IDs that are NOT disabled
            return this.availableOptions.map(opt => opt.id);
        },
        readOnly() { // makes ALL of them read-only/disabled -- 
            // this is for later... for showing in complete read-only mode
            return this.context.attributes?.disabled ?? false;
        },
        selectDefaultCategories() {
            // TenantRole slugs ('ext_quality', 'ext_sourcing', etc... from selectDefaults prop) - pre-select contacts holding these when a NEW vendor is selected
            // always return Array here - matched against tuObj.tenant_roles[].slug
            // NOTE: we use this in DemoWorkflowRuns, and have a lot of existing tenants with selectDefaults='quality' in the form definition  (but it will be 'ext_quality')
            //       so instead of fixing ALL of those, as of Oct 4 2026 we will automatically add 'ext_quality' if we see 'quality' in the selectDefaults
            // you can remove this extra handling once you get rid of DemoWorkflowRuns, and/or once you update all the DemoWorkflowFormVersions that use them (only one really)
            let finalSlugs = [];
            if (this.selectDefaults) {
                if (Array.isArray(this.selectDefaults)) {
                    finalSlugs = [...this.selectDefaults];
                    if (this.selectDefaults.includes('quality') && !this.selectDefaults.includes('ext_quality')) {
                        finalSlugs.push('ext_quality');
                    }
                    if (this.selectDefaults.includes('sourcing') && !this.selectDefaults.includes('ext_sourcing')) {
                        finalSlugs.push('ext_sourcing');
                    }
                } else {   // could be single string here
                    finalSlugs = [this.selectDefaults];
                    if (this.selectDefaults === 'quality') {
                        finalSlugs.push('ext_quality');
                    }
                    if (this.selectDefaults === 'sourcing') {
                        finalSlugs.push('ext_sourcing');
                    }
                }
            }
            // return this.selectDefaults ? (Array.isArray(this.selectDefaults) ? [...this.selectDefaults] : [this.selectDefaults]) : [];  
            return finalSlugs;
        },
    },
    watch: {
        'formComponent.proxy': {
            handler: function() {
                // this gets triggered at the right time (assuming we found the parent form component)
                // but we can't tell difference between old/new values here -- so call handleVendorUpdate() -- that will update if needed
                //console.log('STARTING WATCH HANDLER ON formComponentData');
                this.handleVendorUpdate();
            },
            deep: true,
            immediate: true,
        },
        // TODO - also need to watch for changes in context.model -- if it changes, call setSelected() with new value???
        //      - maybe... if so, then would need to make sure it doesn't interfere with stuff going on in checkAndSetInitialData
    },
    created() {
        //console.log('contact picker created - context.model', JSON.stringify(this.context.model));
        // 'initialize' context.model to be an array if it's not already, otherwise I think we have a reactive problem in parent form
        if (!this.context.model) {
            // only do this if you didn't already have a value set in context.model 
            this.context.model = [];
            // do the rest of setting initial stuff in mounted() (checkAndSetInitialData)
        }
        // do not set this.selected here -- we need to check initial data first, then we can set it    
        // NOTE - the initial selection MIGHT INCLUDE EXTRA ONES THAT ARE NO LONGER OPTIONS - like if vendor was changed from last time this was used to pick contacts
        // so ideally we filter out invalid options before setting this.selected -- but we don't have options from API yet
        // so we must at least filter them out before setting context.model again, or even better, don't set this.selected until we know options (both probably)
        // DO THIS IN checkAndSetInitialData - this.selected = [...this.context.model];  // set the local value
        this.findFormComponent();  // we have $parent in created already, so find the form here, then in later step we can check initial data
    },
    mounted() {
        this.checkAndSetInitialData();
    },
    methods: {
        async checkAndSetInitialData() {
            // check if we have initial data for this field, and for vendor field, see if they match (contact vendor and vendor)
            // call in mounted() hook -- once we know initial data and know we are mounted
            // also set this.selected here -- from initial this.context.model -- but make sure we remove any selections that are no longer options
            // this should be the first place taht this.selected is set
            //console.log('hello from checkAndSetInitialData');
            const hasInitialValue = this.context.model && Array.isArray(this.context.model) && !!this.context.model.length;
            if (!this.selectedVendorId) {
                // if NO selected vendor, then we should NOT have any selected contacts -- clear them if we do (can clear them either way) but its alarming if we have contacts but no vendor
                if (hasInitialValue) {
                    // have initial contacts selected, but no vendor?  that's not cool... gotta at least clear selections
                    console.warn(`we HAVE selected contacts, but NO SELECTED VENDOR - that's unexpected!`);
                    this.clearSelected();
                } else {
                    // if no selected vendor, and no initial value, then we are done -- no need to fetch options, but we'll still set selected to empty array
                    this.clearSelected();  // that also updates context.model
                }
            } else {
                // got a selected vendor?  then we must fetch options, no matter what (if we have initial value or not)
                // but we will only select default contacts if we don't already have selected contacts
                await this.getOptions({setDefaultContacts: !hasInitialValue});
                console.log('DONE WITH getOptions in checkAndSetInitialData - now checking selected contacts');
                // if we did NOT have an initial value, then getOptions will have set the default contacts, we should be good
                // if we DID have an initial value, then we need to check if they are still valid options
                if (!hasInitialValue) {
                    console.log(`in checkAndSetInitialData -- we have selectedVendorId, but NO INITIAL VALUE - we should have already selected defaults, so we are done`);
                    //this.selectDefaultContacts();  // should have already happened in getOptions
                } else {
                    // in here we DO have contacts selected -- we have non-empty array in this.context.model
                    // we DO have contacts selected?  make sure they match the selected vendor!  AND are still possible options!
                    // vendor ID will be in each tuObj.company
                    const hasFetchedOptions = this.fetchedOptions && Array.isArray(this.fetchedOptions) && !!this.fetchedOptions.length;
                    if (!hasFetchedOptions) {
                        // no fetched options?  but we have selected ones?  that's a problem... clear selections
                        console.warn(`have selected vendor AND selected contacts, but no options after fetching from API! that's unexpected`);
                        this.clearSelected();
                    } else {  
                        // otherwise, set initial value of this.selected based on context.model, but only if they are valid options
                        // 1.  sanity check to make sure all selected contacts are from the selected vendor -- should always be the case
                        const tuCompanyIds = this.fetchedOptions.map(tuObj => tuObj.company);
                        const vendorMatch = tuCompanyIds.every(compId => compId == this.selectedVendorId);
                        if (!vendorMatch) {
                            console.warn(`Found selected contacts are NOT MATCHING selectedVendorId of ${this.selectedVendorId}`);
                            // this could happen if the vendor no longer has any active TUs, etc.
                            this.clearSelected();
                        } else {
                            console.log(`We have selected contacts AND selected vendor, and they match!  All good.  Dumping selected contacts next:`);
                        }
                        // 2. Set this.selected to the filtered list of context.model that are in fetchedOptions
                        // now filter initial selections from this.context.model, only allowing those that are in our options (and not disabled)
                        // we only get here if we have selection in this.context.model
                        this.setSelected(this.context.model);  // this will update this.selected AND this.context.model, and make sure all are valid options
                    }
                }
            }
            this.initialCheckComplete = true;  // finally, set this true to show stuff
        },
        findFormComponent() {
            // climb the component tree until you find the parent FormulateForm component, then stash it on this.formComponent
            // this also sets the selectedVendorId 
            let maybeParent = this.$parent;
            while (maybeParent) {
                console.log('checking this parent', maybeParent);
                if (maybeParent.$options.name == 'FormulateForm') {
                    console.log('FOUND PARENT FORM!', maybeParent);
                    this.formComponent = maybeParent;
                    this.formComponentInitialData = this.formComponent.initialValues;
                    //console.log('formComponentInitialData', JSON.stringify(this.formComponentInitialData));
                    this.formComponentData = this.formComponent.proxy;
                    //console.log('formComponentData', JSON.stringify(this.formComponentData));
                    // also update this.selectedVendorId immediately here, don't wait for watcher to see it
                    // NOTE - we are attaching handler in THIS component to 'input' event in parent form -- is that cool?
                    this.formComponent.$on('input', this.handleVendorUpdate);
                    this.selectedVendorId = this.getVendorId();
                    break;
                } else {
                    maybeParent = maybeParent.$parent;
                }
            }
        },
        formDidSomething() {
            //console.log('formDidSomething');
        },
        getVendorId() {
            // get and return the current vendor ID that SHOULD be selected (based on priority of where it comes from)
            // but don't set it on this instance yet - just return it to caller
            //console.log('starting getVendorId');
            const vendorIdFromFormData = this.formComponentData?.[String(this.vendorField)];
            //console.log('vendorIdFromFormData', vendorIdFromFormData);
            const vendorIdFromFormInitialData = this.formComponentInitialData?.[String(this.vendorField)];
            //console.log('vendorIdFromFormInitialData', vendorIdFromFormInitialData);
            const currentVendorId = this.vendorId ?? vendorIdFromFormData ?? vendorIdFromFormInitialData ?? '';
            return currentVendorId;
        },
        handleVendorUpdate() {
            // IGNORE ANYTHING THAT CALLS THIS (like your watcher) UNTIL this.initialCheckComplete = true
            // or else we'll clear out the things we want to check....
            console.log('starting handleVendorUpdate');
            if (!this.initialCheckComplete) {
                console.log('initialCheckComplete is false');
                return;
            }
            //console.log('handleVendorUpdate');
            // call this anytime you THINK there is a vendor update -- this checks it and updates local data if it DID change
            const newVendorId = this.getVendorId();
            console.log('newVendorId = ', newVendorId);
            if (newVendorId) {
                if (newVendorId !== this.selectedVendorId) {
                    // if it DID CHANGE and we have a new selection
                    console.log(`Looks like WE DID change selected vendor -- from ${this.selectedVendorId} to ${newVendorId}`);
                    this.selectedVendorId = newVendorId;
                    this.clearSelected();  // clear selected first, then once we get new options we'll set pre-selected defaults if any
                    this.getOptions({setDefaultContacts: true});  // will also set default contacts since we just cleared selected -- returns promise, in case you need to chain anything after this?
                } else {
                    console.log(`Looks like we DID NOT change selected vendor - so not re-fetching options or clearing selected`);
                    return;
                }
            } else if (!newVendorId && this.selectedVendorId) {
                // if newVendorId is empty (but local selectedVendorId is NOT), clear ID and options and selected
                // have to check both, or else we get infinite loop
                this.selectedVendorId = '';
                this.clearSelected();
                this.fetchedOptions = [];
            }
        },
        async getOptions({setDefaultContacts = true} = {}) {
            console.log('fROM FormulateVendorContactPicker - starting getOptions');
            this.loadingOptions = true;
            try {
                const { data } = await suppliosApi.vendorStandard.listContacts(this.selectedVendorId);
                this.fetchedOptions = [ ...data ];  // not paginated  
                // HANDLE SELECTING DEFAULTS 
            } catch (err) {
                this.fetchedOptions = [];  // reset this, not sure what happened...
                this.apiErrorHandler(err);
            }
            if (setDefaultContacts) {
                console.log('FROM FormulateVendorContactPicker - setDefaultContacts is truthy - so calling selectDefaultContacts');
                this.selectDefaultContacts();  // select the default contacts if no already-selected ones
            }
            this.loadingOptions = false;
        },
        selectDefaultContacts() {
            // this should only be done when you don't already have any selected contacts
            // but you are doing that in the caller... do here instead???
            //console.log('starting selectDefaultContacts');
            const selectIds = [];
            if (this.selectDefaultCategories.length) {
                this.fetchedOptions.forEach(tuObj => {
                    const heldRoleSlugs = (tuObj.tenant_roles || []).map(role => role.slug);
                    const shouldSelect = this.selectDefaultCategories.some(def => heldRoleSlugs.includes(def));
                    if (shouldSelect && !this.isOptionDisabled(tuObj)) {
                        selectIds.push(tuObj.id);
                    }
                });
            }
            console.log('FROM selectDefaultContacts - selected these IDs', selectIds);
            this.setSelected(selectIds);
        },
        clearSelected() {
            console.log('CLEARING SELECTED');
            this.setSelected([]);
        },
        setSelected(selectedIds) {
            // set the selected IDs -- called from various places to update the local value AND set this.context.model
            // it handles making sure the new selectedIds are valid options (not disabled, correct company ID, etc.)
            console.log('starting setSelected', selectedIds);
            const validSelectedIds = selectedIds.filter(selId => this.availableOptionIds.includes(selId));
            this.selected = validSelectedIds;
            // TODO?  should you check if there is any difference in values (other than ordering) before updating context.model?
            this.updateModel(validSelectedIds);
        },
        updateModel(selected) {
            console.log('in FormulateVendorContactPicker - starting updateModel:', selected);
            // should be an array -- need this in addition to v-model (v-model is required when using a v-for of checkboxes, i believe)
            // the 'local' value (selected) will get updated in v-model
            // in here, we update the context.model
            console.log('current context.model before updating in updateModel:', this.context.model);
            const newSelected = [ ...selected ];  // shallow copy... maybe not needed, but be safe
            this.context.model = newSelected;
            // NOTE - if you check the value right away, it might still be old value... 
            //    I THINK because of debounce set on all form inputs from the form
        },
        handleNewVendorContact(newContact) {
            // from 'create-complete' event on contact-create-update-modal - payload will be new contact TU object
            //console.log('create-complete done', newContact);
            // ADD NEW TU TO VENDOR MEMBER OPTIONS
            this.fetchedOptions.push(newContact);  // is this reactive???  should be... -- will need to update the computeds
            //this.updateFinalMemberOptions();  // refresh this
            // THEN SELECT THE NEW TU -- will still have to save the new instance data
            this.$nextTick(() => {
                // put in nextTick to make sure it's in the DOM before trying to select it?
                const newSelected = [ ...this.selected, newContact.id ];
                this.setSelected(newSelected);
            });
        },
        isOptionDisabled(option) {
            // pass in a single option object, this returns if it should be disabled for any reason
            // this ONLY checks a single option object -- also need to consider FORM-LEVEL readOnly (this.readOnly)
            return this.disabledField && option[this.disabledField];
        },
    }
}
</script>

<style lang="sass">

.contact-picker-checkbox-prime-group 
    display: flex 
    flex-wrap: wrap 
    gap: 1rem 
    .vendor-contact-checkbox 
        display: flex
        align-items: center 
        padding-left: 0.5rem
        border-radius: 0.5rem
        border: 1px solid transparent
        transition: 0.2s all
        label 
            padding: 0.5rem
            cursor: pointer 
            margin-left: 0
        &.is-enabled 
            cursor: pointer 
        &.is-readonly  // this is on if overall form is read only, or if one option is disabled
            cursor: not-allowed
            label
                cursor: not-allowed 
        &.is-disabled  // for options that are disabled specifically  
            label
                text-decoration: line-through
        &.is-checked 
            background-color: var(--primary-50)
            border-color: var(--primary-400)
            &.is-enabled:hover 
                background-color: var(--primary-100)
                border-color: var(--primary-500)
        &.not-checked 
            background-color: var(--gray-150)
            &.is-enabled:hover 
                background-color: var(--gray-200)
                border-color: var(--gray-400)

// not sure if this is being used anymore...?
.contact-picker-checkbox-form-group 
    > div 
        display: flex 
        flex-wrap: wrap 
        gap: 1rem
    .vendor-contact-checkbox 
        padding: 0.5rem 0.5rem 0.5rem 2.5rem
        border-radius: 0.5rem
        border: 1px solid transparent
        transition: 0.2s all
        cursor: pointer 
        label 
            cursor: pointer 
        &.is-checked 
            background-color: var(--primary-50)
            border-color: var(--primary-400)
            &:hover 
                background-color: var(--primary-100)
                border-color: var(--primary-500)
        &.not-checked 
            background-color: var(--gray-150)
            &:hover 
                background-color: var(--gray-200)
                border-color: var(--gray-400)

</style>